import { Group, Object3D, Vector3 } from "three";
import IsoBlock from "./IsoBlock";
import animate from "./animate";
import delay from "./delay";
import { easeInCubic, easeOutCubic } from "@shared/lib/animation/easing";

export interface IsoInvestmentGraphOptions {
  color: string,
  minHeight?: number,
  maxHeight?: number,
}

export default class IsoInvestmentGraph {
  group: Group;
  blocks: Array<IsoBlock>;
  blockCount = 5;
  sequenceDelay = 150;
  minHeight = 1;
  maxHeight = 3;

  constructor (parent: Object3D, options: IsoInvestmentGraphOptions) {
    this.group = new Group();
    this.minHeight = options.minHeight || 1;
    this.maxHeight = options.maxHeight || 3;

    this.blocks = Array(this.blockCount).fill(0)
      .map((_, i) => {
        const block = new IsoBlock(this.group, {
          color: options.color,
          position: new Vector3(0, 0, i * -1.5),
        });
        block.height = this.minHeight === 1 && i === 0 ? 1 : 0;

        return block;
      });

    parent.add(this.group);
  }

  async animateIn (render: () => void): Promise<void> {
    const upDuration = 750;

    this.blocks.forEach(async (block, i) => {
      await delay(i * this.sequenceDelay);

      const startHeight = block.height;

      await animate((t) => {
        block.height = startHeight + ((this.minHeight + (this.maxHeight - this.minHeight) * i / (this.blocks.length - 1)) - startHeight) * t;
        render();
      }, {
        duration: upDuration,
        easing: easeOutCubic,
      });
    });

    await delay(this.blocks.length * this.sequenceDelay + upDuration);
  }

  async animateOut (render: () => void): Promise<void> {
    const downDuration = 750;

    this.blocks.forEach(async (block, i) => {
      await delay(i * this.sequenceDelay);
      await animate((t) => {
        block.height = (1 - t) * (this.minHeight + (this.maxHeight - this.minHeight) * i / (this.blocks.length - 1));
        render();
      }, {
        duration: downDuration,
        easing: easeInCubic,
      });
    });

    await delay(this.blocks.length * this.sequenceDelay + downDuration);
  }

  dispose () {
    this.group.removeFromParent();
    this.blocks.forEach((block) => block.dispose());
    this.blocks.length = 0;
  }
}
